
import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Stethoscope, Video, Phone, Calendar } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { addDays, isWithinInterval, parseISO } from 'date-fns';

// Use the same LocalAppointment interface
interface LocalAppointment {
  id: string;
  patientId: string;
  doctorId: string;
  appointmentDate: string;
  appointmentTime: string;
  appointmentType: string;
  status: string;
  duration: number;
  notes?: string;
  patientName?: string;
  date?: string;
  type?: string;
  time?: string;
}

interface ActiveFilters {
  clinic: boolean;
  video: boolean;
  voice: boolean;
  dateRange: DateRange;
}

interface AppointmentFiltersProps {
  activeFilters: ActiveFilters;
  setActiveFilters: React.Dispatch<React.SetStateAction<ActiveFilters>>;
  appointments: LocalAppointment[];
  setFilteredAppointments: React.Dispatch<React.SetStateAction<LocalAppointment[]>>;
}

const AppointmentFilters: React.FC<AppointmentFiltersProps> = ({
  activeFilters,
  setActiveFilters,
  appointments,
  setFilteredAppointments
}) => {
  // Apply filters when active filters or appointments change
  useEffect(() => {
    const filtered = appointments.filter(appointment => {
      // Check type filter
      const type = appointment.type || appointment.appointmentType;
      if (
        (type === 'clinic' && !activeFilters.clinic) ||
        (type === 'video' && !activeFilters.video) ||
        (type === 'voice' && !activeFilters.voice)
      ) {
        return false;
      }
      
      // Check date range filter
      if (activeFilters.dateRange.from && activeFilters.dateRange.to) {
        const appointmentDate = appointment.date ? 
          new Date(appointment.date) : 
          parseISO(appointment.appointmentDate);
          
        if (!isWithinInterval(appointmentDate, { 
          start: activeFilters.dateRange.from, 
          end: activeFilters.dateRange.to 
        })) {
          return false;
        }
      }
      
      return true;
    });
    
    setFilteredAppointments(filtered);
  }, [activeFilters, appointments, setFilteredAppointments]);
  
  // Toggle type filter
  const toggleTypeFilter = (type: 'clinic' | 'video' | 'voice') => {
    setActiveFilters(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };
  
  // Reset all filters
  const resetFilters = () => {
    setActiveFilters({
      clinic: true,
      video: true,
      voice: true,
      dateRange: {
        from: new Date(),
        to: addDays(new Date(), 14)
      }
    });
  };
  
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-medium text-sm mr-2">Type:</div>
            
            <Button
              variant={activeFilters.clinic ? "default" : "outline"}
              size="sm"
              onClick={() => toggleTypeFilter('clinic')}
              className="flex items-center gap-1"
            >
              <Stethoscope className="h-4 w-4" />
              <span className="hidden sm:inline">In-Clinic</span>
            </Button>
            
            <Button
              variant={activeFilters.video ? "default" : "outline"}
              size="sm"
              onClick={() => toggleTypeFilter('video')}
              className="flex items-center gap-1"
            >
              <Video className="h-4 w-4" />
              <span className="hidden sm:inline">Video</span>
            </Button>
            
            <Button
              variant={activeFilters.voice ? "default" : "outline"}
              size="sm"
              onClick={() => toggleTypeFilter('voice')}
              className="flex items-center gap-1"
            >
              <Phone className="h-4 w-4" />
              <span className="hidden sm:inline">Voice</span>
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="font-medium text-sm mr-2 hidden sm:block">Date Range:</div>
            <DateRangePicker
              value={activeFilters.dateRange}
              onChange={(range) => setActiveFilters(prev => ({ ...prev, dateRange: range }))}
            />
            
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="ml-2"
            >
              Reset
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AppointmentFilters;
